/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/ControllerViews/PipelineOutcomeFailingTaskGrid" />
import { IReportContext } from "Analytics/Shared/Common/Types";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { PipelineOutcomeFailingTaskGridActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Actions/PipelineOutcomeFailingTaskGridActionsCreator";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Resources";
import { PipelineOutcomeFailingTaskGridSource } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Sources/PipelineOutcomeFailingTaskGridSource";
import {
    IPipelineOutcomeFailingTaskGridState,
    PipelineOutcomeFailingTaskGridStore
} from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Stores/PipelineOutcomeFailingTaskGridStore";
import { IPipelineRunData, ITreeData } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Types";
import * as UrlCreator from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/UrlCreator";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TableRow } from "VSSUI/Table";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, ITreeRowDetails, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import { Tooltip } from "VSSUI/TooltipEx";

export interface IPipelineOutcomeFailingTaskGridProps extends IVssComponentProperties {
    store: PipelineOutcomeFailingTaskGridStore;
    headerClassName?: string;
    onDataRendered?: () => void;
    errorText?: string;
    actionCreator: PipelineOutcomeFailingTaskGridActionsCreator;
    reportContext: IReportContext;
}

export interface IPipelineOutcomeFailingTaskGridViewState extends IPipelineOutcomeFailingTaskGridState {
    treeItemProvider: ITreeItemProvider<ITreeData | ObservableValue<ITreeData | undefined>>;
}

export class PipelineOutcomeFailingTaskGrid extends VssComponent<IPipelineOutcomeFailingTaskGridProps, IPipelineOutcomeFailingTaskGridViewState> {
    private taskIdToRunsIdsMap: { [key: string]: string[] } = {};
    private source = new PipelineOutcomeFailingTaskGridSource(this.context.pageContext);
    private _onDataRenderedCalled = false;

    private telemetryService: IVssTelemetryService;

    constructor(props: IPipelineOutcomeFailingTaskGridProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText,
            treeItemProvider: new TreeItemProvider<ITreeData>([])
        } as Readonly<IPipelineOutcomeFailingTaskGridViewState>;
        this.telemetryService = context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPipelineOutcomeFailingTaskGridProps, prevState: IPipelineOutcomeFailingTaskGridViewState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return this._getElementToDisplay();
    }

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }

    private _getTreeColumns = (): ITreeColumn<ITreeData>[] => {
        return [
            {
                id: "task",
                renderCell: renderPipelineData,
                width: -100,
                hierarchical: true,
                name: Resources.TreeColumnName
            }
        ];
    };

    private _getElementToDisplay(): JSX.Element {
        if (this.props.errorText) {
            return <AnalyticsUnavailableMessage suggestion={this.props.errorText} />;
        }
        const failedPipelineTaskIds = this.state.data;
        return (
            <>
                {failedPipelineTaskIds && (
                    <>
                        {failedPipelineTaskIds.length > 0 && (
                            <>
                                <Tooltip overflowOnly={true} text={Resources.TreeColumnName}>
                                    <div className="pipeline_outcome-table-header-title text-ellipsis body-s" role="heading" aria-level={2}>
                                        <span>{Resources.TreeColumnName}</span>
                                    </div>
                                </Tooltip>
                                <div className="pipeline_outcome-table-header-separator"></div>
                                <Tree
                                    columns={this._getTreeColumns()}
                                    itemProvider={this.state.treeItemProvider}
                                    onToggle={this._onToggle}
                                    scrollable={true}
                                    onActivate={this._onActivateRow}
                                    renderLoadingRow={this._renderLoadRow}
                                    ariaLabel={Resources.PipelineOutcomeFailingTaskGridAriaLabelText}
                                    showHeader={false}
                                />
                            </>
                            
                        )}
                        {failedPipelineTaskIds.length == 0 && <AnalyticsUnavailableMessage suggestion={Resources.NoTaskFailures} />}
                    </>
                )}
                {failedPipelineTaskIds == null && (
                    <Spinner className={css("analytics-card-spinner flex-grow flex-column")} size={SpinnerSize.large} />
                )}
            </>
        );
    }

    private _renderLoadRow = (rowIndex: number, details: ITreeRowDetails<ITreeData>): JSX.Element => {
        return (
            <TableRow className="bolt-list-row-loading" details={details} index={rowIndex}>
                <td>
                    <Spinner className={css("analytics-card-spinner flex-grow flex-column")} size={SpinnerSize.large} />
                </td>
            </TableRow>
        );
    };

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITreeRow<ITreeData>) => {
        const pageContext = this.context.pageContext;

        const pipelineRunId = row.data.underlyingItem.data.pipelineRunId || null;
        if (pipelineRunId !== null) {
            const link = UrlCreator._getBuildUrl(pipelineRunId, pageContext);
            onClickFPS(pageContext, link, true, event);
            this.telemetryService.publishEvent(TelemetryConstants.area, TelemetryConstants.feature_PipelineTaskGrid, {
                Event: TelemetryConstants.RowClick,
                PipelineId: this.props.reportContext.definitionId,
                PipelineRunId: pipelineRunId
            });
        } else if (row.data.underlyingItem.data.showMore) {
            const rowData = row.data.parentItem ? row.data.parentItem.underlyingItem : undefined;
            if (rowData && rowData.childItems) {
                let insertAfter = rowData.childItems[rowData.childItems.length - 1];
                const loadingRow: ITreeItem<ITreeData | ObservableValue<ITreeData | undefined>> = {
                    data: new ObservableValue<ITreeData | undefined>(undefined)
                };
                this.state.treeItemProvider.splice(rowData, [row.data.underlyingItem], [{ items: [loadingRow], insertAfter }]);
                insertAfter = rowData.childItems[rowData.childItems.length - 1];
                this._addRunsDataToTask(rowData, insertAfter);
            }
        } else {
            this._getChildrenAndToggle(row.data.underlyingItem);
        }
    };

    private async _setTreeItemProvider(): Promise<void> {
        const rootItems: ITreeItem<ITreeData>[] = [];
        const failedPipelineTaskIds = this.state.data;
        var isAllStageOptionSelected = false;
        if(this.state.confValues == null) {
            isAllStageOptionSelected = true;
        } else {
            const stage = this.state.confValues.stage;
            if(stage == null || stage.name == Resources.AllStagesText) {
                isAllStageOptionSelected = true;
            }
        }
        if (failedPipelineTaskIds) {
            failedPipelineTaskIds.forEach(task => {
                this.taskIdToRunsIdsMap[task.taskName] = [];
                var description = task.taskName;
                if(isAllStageOptionSelected) {
                    description = task.stageDisplayName + "\\" + task.taskName
                }
                rootItems.push({
                    childItems: [],
                    data: { description: description, stageName: task.stageName, isTask: true, showMore: false }
                });
            });

            this.setState({ treeItemProvider: new TreeItemProvider<ITreeData>(rootItems) });
        }
    }

    private async _fetchPipelineRunsData(treeItem: ITreeItem<ITreeData>) {
        const pipelineRunIds = treeItem.data.pipelineRunIdList;
        if (pipelineRunIds) {
            const routeValues: { [key: string]: string } = {};
            if (pipelineRunIds) {
                routeValues.buildIds = pipelineRunIds.toString();
            }
            const runsData = await this.source.getPipelineRuns(this.context.pageContext, routeValues, true);
            const runs: IPipelineRunData[] = runsData.runs || [];
            treeItem.data.pipelineRunIdList = runsData.runs ? pipelineRunIds.splice(runsData.runs.length) : undefined;
            treeItem.data.showMore = !!runsData.continuationToken ? true : false;
            return runs;
        }
        return [];
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState(), () => {
            this._setTreeItemProvider();
        });
    };

    private _getChildrenAndToggle(treeItem: ITreeItem<ITreeData>): void {
        if (!treeItem.expanded && treeItem.childItems && treeItem.childItems.length <= 0) {
            const loadingRow: ITreeItem<ITreeData | ObservableValue<ITreeData | undefined>> = {
                data: new ObservableValue<ITreeData | undefined>(undefined)
            };
            this.state.treeItemProvider.splice(treeItem, undefined, [{ items: [loadingRow] }]);

            this._getChildren(treeItem);
        }

        this.state.treeItemProvider.toggle(treeItem);
        this.telemetryService.publishEvent(TelemetryConstants.area, TelemetryConstants.feature_PipelineTaskGrid, {
            Event: TelemetryConstants.RowToggle,
            PipelineId: this.props.reportContext.definitionId,
            TaskName: treeItem.data.description
        });
    }

    private _onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<ITreeData>): void => {
        this._getChildrenAndToggle(treeItem.underlyingItem);
    };

    private async _getChildren(treeItem: ITreeItem<ITreeData>): Promise<void> {
        const pipelineRunIds = await this._fetchPipelineRunIds(treeItem);
        treeItem.data.pipelineRunIdList = pipelineRunIds ? pipelineRunIds.reverse() : pipelineRunIds;
        this._addRunsDataToTask(treeItem);
    }

    private async _addRunsDataToTask(treeItem: ITreeItem<ITreeData>, insertAfter?: ITreeItem<ITreeData>): Promise<void> {
        const pipelineRunDataForIds = await this._fetchPipelineRunsData(treeItem);
        this._buildTree(treeItem, pipelineRunDataForIds, insertAfter);
    }

    private _buildTree(treeItem: ITreeItem<ITreeData>, pipelineRunsData: IPipelineRunData[], insertAfter?: ITreeItem<ITreeData>) {
        const rootItems: ITreeItem<ITreeData>[] = [];
        pipelineRunsData.forEach(pipelineRunData => {
            rootItems.push({
                data: {
                    description: Utils_String.localeFormat(
                        Resources.BuildFormat,
                        pipelineRunData.run.buildNumber,
                        pipelineRunData.sourceVersionMessage
                    ),
                    isTask: false,
                    pipelineRunId: pipelineRunData.run.id,
                    sourceBranch: pipelineRunData.run.sourceBranch,
                    triggerInfo: pipelineRunData.run.triggerInfo,
                    commitId: pipelineRunData.run.sourceVersion,
                    commitUrl: UrlCreator._getcommitUrl(
                        pipelineRunData.run.sourceVersion,
                        this.context.pageContext,
                        pipelineRunData.run.repository.name
                    ),
                    repositoryUrl: UrlCreator._getRepositoryUrl(
                        this.context.pageContext,
                        pipelineRunData.run.repository.name,
                        pipelineRunData.run.sourceBranch
                    ),
                    repositoryType: pipelineRunData.run.repository.type,
                    pullRequestUrl: UrlCreator._getPullRequestUrl(
                        this.context.pageContext,
                        pipelineRunData.run.triggerInfo["pr.number"],
                        pipelineRunData.run.repository.name
                    )
                }
            });
        });
        if (pipelineRunsData.length === 0) {
            rootItems.push({
                data: {
                    description: Resources.NoRunsDataText,
                    isTask: false,
                    noRunsData: true
                }
            });
        }
        if (treeItem.data.showMore) {
            rootItems.push({
                data: {
                    description: Resources.ShowMore,
                    isTask: false,
                    showMore: true
                }
            });
            treeItem.data.showMore = false;
        }

        const itemToRemove = [treeItem.childItems![treeItem.childItems!.length - 1]];

        this.state.treeItemProvider.splice(
            treeItem,
            itemToRemove,
            insertAfter
                ? [
                      {
                          items: rootItems,
                          insertAfter
                      }
                  ]
                : [{ items: rootItems }]
        );
    }

    private async _fetchPipelineRunIds(treeItem: ITreeItem<ITreeData>) {
        try {
            const pipelineRunIds = await this.source.queryFailingPipelineRunIdsForTask(
                this.props.reportContext,
                this.props.store.getState().confValues!,
                treeItem.data.stageName!,
                this._getTaskNameFromStagePlusTaskName(treeItem.data.description)
            );
            return pipelineRunIds;
        } catch (error) {
            const errorText: string = error.toString();

            this.setState({ errorText: errorText });
        }
    }

    private _getTaskNameFromStagePlusTaskName(stagePlusTaskName: string): string {
        var indexOfSlash: number = stagePlusTaskName.indexOf("\\");
        if (indexOfSlash === stagePlusTaskName.length - 1) {
            return "";
        }
        return stagePlusTaskName.substr(indexOfSlash + 1);
    }
}

function renderPipelineData(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<ITreeData>,
    treeItem: ITreeItemEx<ITreeData>,
    ariaRowIndex: number,
    role: string
): JSX.Element {
    const content: JSX.Element = (
        <Observer item={treeItem.underlyingItem}>
            {() => {
                return (
                    <div>
                        {_getTreeRowHeader(treeItem.underlyingItem)}
                        {!treeItem.underlyingItem.data.isTask && !treeItem.underlyingItem.data.showMore && !treeItem.underlyingItem.data.noRunsData && (
                            <div className="flex">
                                {_getMergeComponent(treeItem.underlyingItem)}
                                {treeItem.underlyingItem.data.commitId != null && _getCommitComponent(treeItem.underlyingItem)}
                            </div>
                        )}
                    </div>
                );
            }}
        </Observer>
    );

    return ExpandableTreeCell({
        children: content,
        columnIndex,
        contentClassName: undefined,
        treeColumn,
        treeItem,
        role: role
    });
}

function _getTreeRowHeader(treeItem: ITreeItem<ITreeData>): JSX.Element {
    if (treeItem.data.isTask) {
        return <div className="font-size-m">{treeItem.data.description}</div>;
    } else if (treeItem.data.showMore) {
        return (
            <FPSLink className="font-size-ms text-ellipsis bolt-table-link bolt-table-inline-link" excludeTabStop>
                {treeItem.data.description}
            </FPSLink>
        );
    } else {
        return <div className="title-xs">{treeItem.data.description}</div>;
    }
}

function _getCommitComponent(treeItem: ITreeItem<ITreeData>): JSX.Element {
    const commitId = treeItem.data.commitId!.slice(0, 7);
    return treeItem.data.repositoryType != null && treeItem.data.repositoryType.toLowerCase() === tfsGitRepoType ? (
        <FPSLink className="font-size-ms text-ellipsis bolt-table-link bolt-table-inline-link" excludeTabStop href={treeItem.data.commitUrl}>
            {commitId}
        </FPSLink>
    ) : (
        <div className="font-size-ms text-ellipsis pipeline_outcome-runs-data">{commitId}</div>
    );
}

function _getMergeComponent(treeItem: ITreeItem<ITreeData>): JSX.Element {
    return treeItem.data.sourceBranch && Utils_String.startsWith(treeItem.data.sourceBranch, "refs/pull/")
        ? _fetchMergeLinkForRepo(
              treeItem.data.triggerInfo!["pr.number"],
              treeItem.data.repositoryType!,
              treeItem.data.pullRequestUrl!,
              "BranchPullRequest"
          )
        : _fetchMergeLinkForRepo(
              _fetchRepoName(treeItem.data.sourceBranch),
              treeItem.data.repositoryType!,
              treeItem.data.repositoryUrl!,
              "OpenSource"
          );
}

function _fetchRepoName(sourceBranch: string | undefined) {
    return sourceBranch ? sourceBranch.slice("refs/heads/".length) : "";
}

function _fetchMergeLinkForRepo(text: string, repoType: string, href: string, iconName?: string): JSX.Element {
    return repoType != null && repoType.toLowerCase() === tfsGitRepoType ? (
        <FPSLink className="font-size-ms secondary-text bolt-table-link bolt-table-inline-link" excludeTabStop href={href}>
            {iconName && <Icon iconName={iconName} />}
            {text}
        </FPSLink>
    ) : (
        <div className="font-size-ms text-ellipsis">
            {iconName && <Icon iconName={iconName} />}
            {text}
        </div>
    );
}

const tfsGitRepoType = "tfsgit";
